import { useEffect, useState } from 'react';
import { isRealDonation } from '@/lib/donations/realDonation';
import { Search } from 'lucide-react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useAuth } from '@/components/AuthProvider';
import { fetchDonorContacts } from '@/lib/firebase/data/donorContacts';
import { useTranslation } from 'react-i18next';

interface AudienceUserRow {
    id: string;
    userId: string;
    name: string;
    email: string;
    joinedAt?: any;
    type: 'free' | 'occasional' | 'monthly';
}

export default function AudienceUsersPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState<AudienceUserRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [missionPath, setMissionPath] = useState<string | undefined>(undefined);
    const { user } = useAuth();
    const { t } = useTranslation();

    // Pre-fetched once on mount so "Copy page link" can call clipboard.writeText
    // synchronously from the click handler: an await beforehand (the old
    // per-click getDoc) breaks the user-activation chain on iOS Safari, so the
    // copy silently no-ops on mobile while it still "works" on desktop Chrome.
    // Ported from sportfund's 2ebea0a.
    useEffect(() => {
        if (!user?.uid) return;
        getDoc(doc(db, 'users', user.uid)).then(snap => {
            if (snap.exists()) setMissionPath((snap.data() as any).path);
        }).catch(() => { /* falls back to uid below */ });
    }, [user?.uid]);

    useEffect(() => {
        async function load() {
            if (!user?.uid) return;
            try {
                // Aggregate from donations and subscriptions
                const donationsSnap = await getDocs(query(collection(db, 'donations'), where('creatorId', '==', user.uid)));
                // A row Stripe has never confirmed is not a donation, and the
                // person behind it is not a supporter -- see
                // lib/donations/realDonation. Without this an abandoned checkout
                // promoted a free follower to "occasional" or "monthly" here,
                // while the contributions list next door had already stopped
                // counting it.
                const donations = donationsSnap.docs
                    .map(d => ({ id: d.id, ...(d.data() as any) }))
                    .filter(isRealDonation);
                const userIdToType: Record<string, AudienceUserRow['type']> = {};
                const userIdToJoined: Record<string, any> = {};
                for (const d of donations) {
                    if (!d.userId) continue;
                    userIdToJoined[d.userId] = userIdToJoined[d.userId] || d.createdAt;
                    if (d.frequency === 'monthly') userIdToType[d.userId] = 'monthly';
                    else if (!userIdToType[d.userId]) userIdToType[d.userId] = 'occasional';
                }
                const subsSnap = await getDocs(query(collection(db, 'subscriptions'), where('creatorId', '==', user.uid)));
                for (const s of subsSnap.docs) {
                    const data: any = s.data();
                    if (!data.userId) continue;
                    if (!userIdToType[data.userId]) userIdToType[data.userId] = 'free';
                    userIdToJoined[data.userId] = userIdToJoined[data.userId] || data.createdAt;
                }
                const uniqueUserIds = Array.from(new Set(Object.keys(userIdToType)));
                // One server-side call instead of one /users read per supporter.
                // This screen needs only displayName + email; reading the whole
                // donor document from the client is what forced the /users rule
                // open to every signed-in account.
                const contacts = await fetchDonorContacts(uniqueUserIds);
                const built: AudienceUserRow[] = uniqueUserIds.map((uid) => {
                    const contact = contacts[uid];
                    const name = contact?.displayName || 'Anonymous';
                    const email = contact?.email || '';
                    return { id: uid, userId: uid, name, email, joinedAt: userIdToJoined[uid], type: userIdToType[uid] };
                });
                setRows(built);
            } finally {
                setIsLoading(false);
            }
        }
        load();
    }, [user?.uid]);

    const filtered = rows.filter(r => {
        const s = searchTerm.toLowerCase();
        return r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s);
    });

    return (
        <div className="min-h-screen bg-white py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">{t('audience.title')}</h1>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder={t('audience.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="bg-white rounded-lg border">
                    <div className="grid grid-cols-4 gap-4 p-4 border-b text-sm font-medium">
                        <div className="col-span-1">{t('audience.table.name')}</div>
                        <div className="col-span-1">{t('audience.table.email')}</div>
                        <div className="col-span-1">{t('audience.table.joinDate')}</div>
                        <div className="col-span-1">{t('audience.table.type')}</div>
                    </div>
                    {isLoading ? (
                        <div className="p-12 text-center">
                            <div className="inline-flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                            </div>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-12">
                            <div className="max-w-2xl mx-auto text-center">
                                <div className="bg-gray-50 border border-dashed rounded-lg p-8">
                                    <h3 className="text-xl font-semibold mb-2">No audience yet</h3>
                                    <p className="text-gray-600 mb-6">Your followers and donors will show up here. Get started by adding content and sharing your site with your friends and family.</p>
                                    <div className="flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90"
                                            onClick={() => { window.location.href = '/creator/content/add'; }}
                                        >
                                            Add content
                                        </button>
                                        <button
                                            type="button"
                                            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                                            onClick={async () => {
                                                if (!user?.uid) return;
                                                const missionUrl = missionPath ? `${window.location.origin}/${missionPath}` : `${window.location.origin}/mission/${user.uid}`;
                                                try {
                                                    await navigator.clipboard.writeText(missionUrl);
                                                } catch {
                                                    // ignore
                                                }
                                            }}
                                        >
                                            Copy page link
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {filtered.map((r) => (
                                <div key={r.id} className="grid grid-cols-4 gap-4 p-4 text-sm">
                                    <div className="col-span-1">{r.name}</div>
                                    <div className="col-span-1">
                                        {r.email ? (
                                            <button
                                                type="button"
                                                className="relative w-full text-left cursor-pointer hover:underline"
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(r.email);
                                                        setCopiedId(r.id);
                                                        setTimeout(() => setCopiedId(null), 1500);
                                                    } catch {
                                                        // ignore
                                                    }
                                                }}
                                            >
                                                <span
                                                    className="block overflow-hidden whitespace-nowrap"
                                                    style={{ WebkitMaskImage: 'linear-gradient(90deg, black 75%, transparent)', maskImage: 'linear-gradient(90deg, black 75%, transparent)' }}
                                                >
                                                    {r.email}
                                                </span>
                                                {copiedId === r.id && (
                                                    <span className="ml-2 text-green-600 text-xs">{t('donations.emailCopied')}</span>
                                                )}
                                            </button>
                                        ) : (
                                            t('audience.table.none')
                                        )}
                                    </div>
                                    <div className="col-span-1">{r.joinedAt?.seconds ? new Date(r.joinedAt.seconds * 1000).toLocaleDateString() : t('audience.table.none')}</div>
                                    <div className="col-span-1 capitalize">{r.type === 'free' ? t('audience.table.freeFollower') : r.type === 'monthly' ? 'Monthly donor' : 'Occasional donor'}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


